import {
    formatInvalidDetailPatientName,
    getInvalidDetailTypeMeta,
} from './invalidDetailsQuery';

const escapeHtml = (value) => {
    if (value == null || value === '') {
        return '';
    }

    return String(value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
};

const buildRecordRows = (records) =>
    records
        .map((item) => {
            const typeMeta = getInvalidDetailTypeMeta(item.type);
            const patientName = formatInvalidDetailPatientName(item.patient || {});

            return `
                <tr>
                    <td>${escapeHtml(typeMeta.label)}</td>
                    <td>${escapeHtml(patientName)}</td>
                    <td>${escapeHtml(item.detail || '—')}</td>
                    <td>${escapeHtml(item.createdAt || '—')}</td>
                </tr>
            `;
        })
        .join('');

const buildPrintDocument = ({ records, title, subtitle }) => {
    const rows = buildRecordRows(records);
    const subtitleHtml = subtitle ? `<p class="subtitle">${escapeHtml(subtitle)}</p>` : '';

    return `<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="utf-8" />
    <title>${escapeHtml(title)}</title>
    <style>
        @page {
            size: letter portrait;
            margin: 0.45in;
        }

        * {
            box-sizing: border-box;
        }

        body {
            margin: 0;
            padding: 0;
            font-family: Arial, Helvetica, sans-serif;
            color: #181c32;
            background: #fff;
        }

        h1 {
            margin: 0 0 0.25rem;
            font-size: 1.1rem;
            font-weight: 700;
        }

        .subtitle {
            margin: 0 0 0.85rem;
            font-size: 0.8rem;
            color: #78829d;
        }

        table {
            width: 100%;
            border-collapse: collapse;
            font-size: 0.78rem;
        }

        thead th {
            text-align: left;
            padding: 0.45rem 0.5rem;
            border-bottom: 2px solid #d8dbe5;
            font-size: 0.68rem;
            text-transform: uppercase;
            letter-spacing: 0.04em;
            color: #78829d;
            white-space: nowrap;
        }

        tbody td {
            padding: 0.5rem;
            border-bottom: 1px solid #eceef3;
            vertical-align: top;
            word-break: break-word;
        }

        tbody tr:nth-child(even) {
            background: #fafbfc;
        }

        .empty {
            padding: 2rem 0;
            text-align: center;
            color: #78829d;
            font-size: 0.9rem;
        }
    </style>
</head>
<body>
    <h1>${escapeHtml(title)}</h1>
    ${subtitleHtml}
    ${
        records.length
            ? `<table>
                <thead>
                    <tr>
                        <th>Type</th>
                        <th>Patient</th>
                        <th>Invalid Detail</th>
                        <th>Created</th>
                    </tr>
                </thead>
                <tbody>${rows}</tbody>
            </table>`
            : '<p class="empty">No invalid details to print.</p>'
    }
</body>
</html>`;
};

const removePrintFrame = (frame) => {
    if (frame?.parentNode) {
        frame.parentNode.removeChild(frame);
    }
};

export const printInvalidDetailsList = ({ records, title, subtitle }) => {
    const html = buildPrintDocument({ records, title, subtitle });

    const iframe = document.createElement('iframe');
    iframe.setAttribute('title', 'Invalid details print preview');
    iframe.setAttribute('aria-hidden', 'true');
    iframe.style.cssText =
        'position:fixed;right:0;bottom:0;width:0;height:0;border:0;visibility:hidden;';

    document.body.appendChild(iframe);

    const frameWindow = iframe.contentWindow;
    const frameDocument = frameWindow?.document;

    if (!frameWindow || !frameDocument) {
        removePrintFrame(iframe);
        return false;
    }

    frameDocument.open();
    frameDocument.write(html);
    frameDocument.close();

    const cleanup = () => {
        removePrintFrame(iframe);
    };

    const triggerPrint = () => {
        try {
            frameWindow.focus();
            frameWindow.print();
        } catch (error) {
            console.error(error);
            cleanup();
        }
    };

    frameWindow.addEventListener('afterprint', cleanup, { once: true });
    window.setTimeout(cleanup, 60000);

    if (frameDocument.readyState === 'complete') {
        window.setTimeout(triggerPrint, 150);
    } else {
        iframe.addEventListener('load', () => window.setTimeout(triggerPrint, 150), { once: true });
    }

    return true;
};

export const buildInvalidDetailsPrintSubtitle = ({
    recordsCount,
    totalElements,
    pageNumber,
    totalPages,
    appliedFilters,
}) => {
    const parts = [];

    if (totalPages > 1) {
        parts.push(`Page ${pageNumber + 1} of ${totalPages}`);
    }

    parts.push(`${recordsCount} record${recordsCount === 1 ? '' : 's'} shown`);

    if (totalElements > recordsCount) {
        parts.push(`${totalElements} total matching filters`);
    }

    if (appliedFilters?.patientName?.trim()) {
        parts.push(`Patient: ${appliedFilters.patientName.trim()}`);
    }

    if (appliedFilters?.detail?.trim()) {
        parts.push(`Detail: ${appliedFilters.detail.trim()}`);
    }

    if (appliedFilters?.type && appliedFilters.type !== 'ALL') {
        parts.push(`Type: ${getInvalidDetailTypeMeta(Number(appliedFilters.type)).label}`);
    }

    if (appliedFilters?.date) {
        parts.push(`Date: ${appliedFilters.date}`);
    }

    return parts.join(' · ');
};
